import * as React from "react";
import styled from "@emotion/styled";
import tw from "twin.macro";
import { useNavigate } from "react-router-dom";

import Modal from "app/components/ui/Modal";
import Announcement from "app/models/announcement";
import { useAppTheme } from "app/providers/AppThemeProvider";
import Header from "app/styles/Header";
import Text from "app/styles/Text";
import Button from "app/components/ui/Button";
import media from "app/styles/media";
import SubHeader from "app/styles/SubHeader";
import StepIndicator from "./StepIndicator";
import { ReactComponent as CaretRight } from "app/assets/icons/caret-right.svg";
import { ExploreAnnouncement } from "app/models/banner";

interface Props {
  open: boolean;
  onClose: () => void;
  setIsOpen: (b: boolean) => void;
  config: ExploreAnnouncement[];
  isDismissed: boolean;
}

export default function AnnouncementModal(
  props: React.PropsWithChildren<Props>
) {
  const { open, onClose, isDismissed, setIsOpen, config } = props;
  const navigate = useNavigate();
  const [activeFrame, setActiveFrame] = React.useState(0);

  const theme = useAppTheme();

  const onAction = (a: ExploreAnnouncement) => {
    onClose();
    if (a.meta.web_link.startsWith("http")) {
      window.open(a.meta.web_link, "_blank");
    } else {
      navigate(a.meta.web_link);
    }
  };

  const contentRef = React.useRef(null);

  const onPrev = () => {
    setActiveFrame((s) => s - 1);
  };

  const onNext = () => {
    setActiveFrame((s) => s + 1);
  };

  React.useEffect(() => {
    const targetElement = contentRef.current;

    if (isDismissed) return;

    const openDelayedModal = async () => {
      await Promise.resolve();
      setIsOpen(true);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            openDelayedModal();
          }
        });
      },
      {
        root: document.querySelector("main.content"),
        threshold: 0,
      }
    );

    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => {
      observer.disconnect();
    };
  }, [isDismissed, setIsOpen]);

  return (
    <>
      <div ref={contentRef}></div>

      <FeatureDialog
        tw="max-h-full"
        open={open}
        onClose={onClose}
        className={config.length > 1 ? "multiple" : "single"}
        title="✨   New features for you "
      >
        {/* Single announcement  */}

        {config.length === 1 &&
          config.map((c) => {
            return (
              <Container>
                <div className="main">
                  <GIFBox>
                    <img
                      src={
                        theme === "light"
                          ? c.meta?.image
                          : c.meta?.dark_mode_image
                      }
                      alt={c.title}
                      loading="eager"
                      tw="mobile:hidden w-full h-[320px] object-center object-cover"
                    />

                    <img
                      src={
                        theme === "light"
                          ? c.meta?.image
                          : c.meta?.dark_mode_image
                      }
                      loading="eager"
                      alt={c.title}
                      tw="hidden mobile:block"
                    />
                  </GIFBox>

                  <Actions>
                    <Header semiBold>{c.title}</Header>

                    {typeof c.meta.description === "string" ? (
                      <Text subdued>
                        {c.meta.description.replace(/<[^>]*>/g, "")}
                      </Text>
                    ) : (
                      c.meta.description
                    )}

                    <div tw="flex w-full mt-[24px] gap-[16px] mobile:flex-col-reverse">
                      <Button tw="flex-1" tertiary onClick={onClose}>
                        {c.meta.cancel_text || "Dismiss"}
                      </Button>

                      <Button tw="flex-1" primary onClick={() => onAction(c)}>
                        {c.meta.action_text}
                      </Button>
                    </div>
                  </Actions>
                </div>
              </Container>
            );
          })}

        {/* Multiple announcements */}

        {config.length > 1 && (
          <div tw=" pt-[16px] mobile:pt-0 pb-[24px]">
            <div tw="flex justify-between items-center gap-[24px] mobile:flex-col border-b border-[#E5E5E5] dark:border-[#1A1A1A] pb-[16px]">
              <div tw="pl-[24px] mobile:px-[16px] mobile:order-last">
                <SubHeader tw="text-[2.4rem] mb-[8px]">
                  {config[activeFrame].title}
                </SubHeader>

                {typeof config[activeFrame].meta.description === "string" ? (
                  <Text subdued medium>
                    {config[activeFrame].meta.description.replace(
                      /<[^>]*>/g,
                      ""
                    )}
                  </Text>
                ) : (
                  config[activeFrame].meta.description
                )}
              </div>
              <div tw="mobile:bg-[#e6f2fd] mobile:dark:bg-[#294960] pr-[24px] mobile:pr-0">
                <img
                  src={
                    theme === "light"
                      ? config[activeFrame].meta.image
                      : config[activeFrame].meta.dark_mode_image
                  }
                  alt={config[activeFrame].title}
                  loading="eager"
                  tw="max-w-[260px] mobile:max-w-full"
                />
              </div>
            </div>

            <div tw="px-[24px] mobile:px-[16px] flex justify-between pt-[16px] items-center">
              <button
                onClick={onPrev}
                disabled={activeFrame === 0}
                tw="disabled:opacity-[0.4] flex gap-[4px] rounded-[4px] items-center text-[14px] font-semibold px-[12px] py-[6px] border border-[#E5E5E5] bg-[#F9F9F9] dark:border-[#1A1A1A] dark:bg-[#0D0D0D]"
              >
                {" "}
                <CaretRight tw="rotate-180 text-black32 dark:text-white32" />{" "}
                Previous
              </button>

              <StepIndicator
                frames={config.length}
                activeFrame={activeFrame + 1}
              />

              <button
                onClick={activeFrame + 1 === config.length ? onClose : onNext}
                tw="disabled:opacity-[0.4] flex gap-[4px] rounded-[4px] items-center text-[14px] font-semibold px-[12px] py-[6px] border border-[#E5E5E5] bg-[#F9F9F9] dark:border-[#1A1A1A] dark:bg-[#0D0D0D]"
              >
                {activeFrame + 1 === config.length ? (
                  "Done"
                ) : (
                  <>
                    Next
                    <CaretRight tw="text-black32 dark:text-white32" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </FeatureDialog>
    </>
  );
}

const FeatureDialog = styled(Modal)`
  .single {
    ${tw`relative w-[544px] bg-[#e6f2fd] dark:bg-[#5A5C5E]`}
  }

  .multiple {
    ${tw`relative w-[720px]`}
  }

  .single {
    .header {
      padding: 16px 30px;
    }
  }

  button[aria-label="Close"] {
    svg {
      path {
        ${tw`dark:fill-[#fff]`};
      }
    }
  }
`;

const Container = styled.div`
  ${tw`relative`}

  > .main {
    ${media.mobile} {
      width: 100%;
    }
  }
`;

const GIFBox = styled.div`
  ${tw`w-full flex justify-center items-center`};
`;

const Actions = styled.div`
  ${tw`w-full bg-white flex flex-col p-[30px] dark:bg-black`};
`;
